import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { Thumb } from './EmblaCarouselThumbsButton'
import { clsx } from 'clsx'
import Zoomable from 'client/fragments/productDetailsFragmentInfo/components/DetailImage/Zoomable'
import { ZoomOutLineIcon, ZoomInLineIcon, CloseLineIcon, ArrowLeftSLineIcon, ArrowRightSLineIcon } from 'client/icons'
import { hideOthers } from 'aria-hidden'
import { createFocusTrap } from 'focus-trap'
import { focusableSelector } from '../../../../common/focus'
import { dictionary } from '../../../../common/accessability'

let undoAriaHidden
let focusTrap
export const EmblaCarousel = ({ slides, options, lang }) => {
  const [zoom, setZoom] = useState(1.0)
  const ref = useRef(null)
  const [isFullScreen, setFullScreen] = useState(false)
  const scrolling = useRef(null)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaMainRef, emblaApi] = useEmblaCarousel(options)
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true
  })
  const hasVideos = useMemo(() => slides.find(it => it.type === 'VIDEO'), [slides])
  const rootRef = useRef()

  const onThumbClick = useCallback(
    index => {
      if (!emblaApi || !emblaThumbsApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi, emblaThumbsApi]
  )

  useEffect(() => {
    const container = rootRef.current
    if (!container || !emblaApi) return
    const handleKeyDown = event => {
      const { key } = event

      if (key === 'ArrowLeft') {
        emblaApi.canScrollPrev() && emblaApi.scrollPrev()
      } else if (key === 'ArrowRight') {
        emblaApi.canScrollNext() && emblaApi.scrollNext()
      }
    }

    container.addEventListener('keydown', handleKeyDown)
    return () => {
      container.removeEventListener('keydown', handleKeyDown)
    }
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi || !emblaThumbsApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
    emblaThumbsApi.scrollTo(emblaApi.selectedScrollSnap())
  }, [emblaApi, emblaThumbsApi, setSelectedIndex])

  // this method is needed for reset outside zoom button, on item scroll(also handle when you scroll next and immediately zoom)
  const onScroll = useCallback(() => {
    if (hasVideos) {
      ;[...document.querySelectorAll('.embla video')].forEach(it => {
        it.pause()
        it.currentTime = 0
      })
    }

    if (!isFullScreen) return
    // (tried to get current slide but during scrolling it's not stable so, cannot be used)
    // as far as scrolling event fired 50-60 times, and we need only first, logic to handle it is below
    if (!scrolling.current) {
      setZoom(1)
    }
    scrolling.current && clearTimeout(scrolling.current)
    scrolling.current = setTimeout(() => {
      scrolling.current = null
    }, 100)
  }, [hasVideos, isFullScreen])

  useEffect(() => {
    if (!emblaApi) return
    // eslint-disable-next-line
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
    emblaApi.on('scroll', onScroll)
  }, [emblaApi, onSelect, onScroll])

  useEffect(() => {
    // eslint-disable-next-line
    setZoom(1.0)
    const element = document.querySelector('.embla')
    if (isFullScreen) {
      undoAriaHidden = hideOthers(rootRef.current)
      element.focus()
      !focusTrap?.active && focusTrap?.activate()
      document.body.classList.add('overflow-hidden')
    }
  }, [isFullScreen])

  useEffect(() => {
    focusTrap = createFocusTrap(document.querySelector('.embla'))
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    // because race conditions of zoomable and embla, we should delay reinit
    setTimeout(() => emblaApi.reInit({ watchDrag: zoom < 1.1 }), 100)
  }, [emblaApi, zoom])

  const selected = emblaApi?.selectedScrollSnap() || 0
  const isZoomed = zoom > 1.1
  const selectedSlide = slides[selected]

  return (
    <div className={clsx('embla', isFullScreen && 'embla-full')} ref={rootRef}>
      {isFullScreen && (
        <div className='full-controls flex justify-end gap-1 m2'>
          {selectedSlide.type !== 'VIDEO' && (
            <button
              onClick={() => {
                isZoomed ? ref?.current?.resetTransform() : ref?.current?.centerView(2)
                setZoom(isZoomed ? 1 : 2)
              }}
            >
              {isZoomed ? <ZoomOutLineIcon /> : <ZoomInLineIcon />}
            </button>
          )}
          <button
            onClick={() => {
              document.body.classList.remove('overflow-hidden')
              setFullScreen(false)
              focusTrap?.active && focusTrap?.deactivate()
              undoAriaHidden?.()
              document.querySelector(`.mxd-product-detail-layout-info ${focusableSelector}`).focus()
            }}
          >
            <CloseLineIcon />
          </button>
        </div>
      )}

      <div className='embla-main relative'>
        {slides.length > 1 && (
          <>
            {emblaApi?.canScrollPrev() && (
              <button
                className='nav-btn left-0'
                title={`${dictionary(lang)['prev-picture']}`}
                onClick={() => emblaApi.scrollPrev()}
              >
                <ArrowLeftSLineIcon />
              </button>
            )}
            {emblaApi?.canScrollNext() && (
              <button
                className='nav-btn right-0'
                title={`${dictionary(lang)['next-picture']}`}
                onClick={() => emblaApi.scrollNext()}
              >
                <ArrowRightSLineIcon />
              </button>
            )}
          </>
        )}

        <div className='overflow-hidden' ref={emblaMainRef}>
          <div className='embla__container'>
            {slides.map((it, i) => {
              const isZoomable = isFullScreen && i === selected
              const Wrapper = isZoomable ? Zoomable : Fragment
              const zoomProps = {
                ref,
                disabled: !isZoomed
              }
              if (it.type === 'VIDEO') {
                return (
                  <div className={clsx('embla__slide')} key={it.url}>
                    <video className='col-12 ' key={it.url} preload='false' controls>
                      <source src={it.url} />
                    </video>
                  </div>
                )
              }

              const AccessibleEl = isFullScreen ? 'div' : 'button'
              return (
                <AccessibleEl
                  className={clsx('embla__slide', isZoomed && 'zoomed')}
                  key={it.url}
                  onClick={() => !isFullScreen && setFullScreen(true)}
                  style={{ margin: '1px' }}
                >
                  <picture>
                    <Wrapper {...(isZoomable && zoomProps)}>
                      <img
                        src={isFullScreen ? it.bigUrl : it.url}
                        alt={it.alt}
                        loading={i === 0 ? 'eager' : 'lazy'}
                        {...(i === 0 && { fetchpriority: 'high' })}
                      />
                    </Wrapper>
                  </picture>
                </AccessibleEl>
              )
            })}
          </div>
        </div>
      </div>

      <div className='embla-thumbs flex justify-center'>
        <div className='overflow-hidden px1' ref={emblaThumbsRef}>
          <div className='flex gap-1 p1'>
            {slides.map((it, index) => (
              <Thumb
                key={it.url}
                onClick={() => onThumbClick(index)}
                selected={index === selectedIndex}
                index={index}
                img={it.smallUrl}
                type={it.type}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmblaCarousel
